import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Stethoscope, Sparkles, ShieldCheck, ArrowRight, AlertTriangle, ChevronLeft, FileText, Loader2, HelpCircle, Paperclip, Download, X } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar as RadarSeries, ResponsiveContainer } from "recharts";
import api, { API } from "../lib/api";
import { PageHeader, Loading, EmptyState, AITag, ApprovedTag } from "../components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const LEVEL_COLORS = { Nascent: "text-rose-600", Emerging: "text-orange-600", Developing: "text-amber-600", Established: "text-sky-600", Leading: "text-emerald-600" };
const SOURCE_STYLES = {
  "client-stated": "bg-sky-50 text-sky-700 border-sky-200",
  "evidence-supported": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "system-inferred": "bg-violet-50 text-violet-700 border-violet-200",
  "adviser-judgement": "bg-amber-50 text-amber-700 border-amber-200",
};

export default function Diagnostic() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [mode, setMode] = useState("list");
  const [selectedId, setSelectedId] = useState(null);

  const { data: list, isLoading } = useQuery({ queryKey: ["diagnostics"], queryFn: async () => (await api.get("/diagnostic")).data });

  if (isLoading) return <Loading />;
  if (mode === "run") return <RunDiagnostic onDone={(d) => { qc.invalidateQueries({ queryKey: ["diagnostics"] }); setSelectedId(d.id); setMode("detail"); }} onCancel={() => setMode("list")} />;
  if (mode === "detail" && selectedId) return <DiagnosticDetail id={selectedId} onBack={() => setMode("list")} nav={nav} />;

  return (
    <div>
      <PageHeader eyebrow="AI Sustainability Diagnostic" title="Diagnostics"
        description="Guided maturity assessments with rule-based scoring, AI-explained findings, and evidence-labelled sources. Never presented as formal assurance."
        actions={<Button onClick={() => setMode("run")} data-testid="new-diagnostic" className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-1.5" /> New diagnostic</Button>} />
      {(list || []).length === 0 ? (
        <EmptyState icon={Stethoscope} title="No diagnostics yet" description="Run a guided sustainability maturity assessment for a client." action={<Button onClick={() => setMode("run")} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-1.5" /> New diagnostic</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((d) => (
            <motion.button key={d.id} onClick={() => { setSelectedId(d.id); setMode("detail"); }} data-testid={`diag-card-${d.id}`}
              className="text-left bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md hover:border-emerald-200 p-6 transition-all">
              <div className="flex items-center justify-between">
                <div className="font-display font-semibold text-slate-900">{d.org_name}</div>
                <span className="text-xs font-mono text-slate-400">v{d.version}</span>
              </div>
              <div className="flex items-end gap-2 mt-3">
                <span className="font-display text-3xl font-bold text-slate-900">{d.scoring.overall_score}%</span>
                <span className={`text-sm font-medium mb-1 ${LEVEL_COLORS[d.scoring.overall_level]}`}>{d.scoring.overall_level}</span>
              </div>
              <div className="mt-3">{d.approved ? <ApprovedTag /> : <AITag />}</div>
            </motion.button>
          ))}
        </div>
      )}
      <p className="mt-8 text-xs text-slate-400 flex items-center gap-1.5"><HelpCircle className="w-3.5 h-3.5" /> Diagnostics are versioned and reproducible. Scores are indicative and reviewed by the adviser.</p>
    </div>
  );
}

function RunDiagnostic({ onDone, onCancel }) {
  const { data: fw } = useQuery({ queryKey: ["framework"], queryFn: async () => (await api.get("/diagnostic/framework")).data });
  const [orgName, setOrgName] = useState("");
  const [answers, setAnswers] = useState({});
  const [evidence, setEvidence] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [tagFor, setTagFor] = useState("general");
  const run = useMutation({ mutationFn: (p) => api.post("/diagnostic/run", p), onSuccess: (r) => onDone(r.data) });

  if (!fw) return <Loading />;
  const setAns = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));
  const answeredCount = Object.keys(answers).length;
  const totalQ = fw.categories.reduce((s, c) => s + c.questions.length, 0);
  const flatQuestions = fw.categories.flatMap((c) => c.questions.map((q) => ({ ...q, category_id: c.id, category_name: c.name })));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const q = flatQuestions.find((x) => x.id === tagFor);
      const item = { ...data, question_id: q ? q.id : null, question_text: q ? q.text : "General evidence",
        category_id: q ? q.category_id : null, category_name: q ? q.category_name : null };
      setEvidence((ev) => [...ev, item]);
      toast.success(`Attached ${data.filename}${q ? ` to "${q.category_name}"` : ""}`);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="max-w-3xl">
      <button onClick={onCancel} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ChevronLeft className="w-4 h-4" /> Back</button>
      <PageHeader eyebrow="New diagnostic" title="Sustainability Maturity Questionnaire" description={`${fw.name} · ${answeredCount}/${totalQ} answered · "Unknown" is always allowed.`} />
      <div className="mb-6"><Label>Client organisation</Label><Input value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="diag-org-name" className="mt-1.5 max-w-md" placeholder="e.g. Meridian Foods" /></div>
      <div className="space-y-6">
        {fw.categories.map((cat) => (
          <div key={cat.id} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-display font-semibold text-slate-900">{cat.name}</h3>
              <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{cat.pillar}</span>
            </div>
            <div className="space-y-5">
              {cat.questions.map((q) => (
                <div key={q.id}>
                  <div className="text-sm font-medium text-slate-700 mb-2">{q.text}</div>
                  <div className="flex flex-wrap gap-2">
                    {q.options.map((opt) => (
                      <button key={opt.value} type="button" onClick={() => setAns(q.id, opt.value)} data-testid={`ans-${q.id}-${opt.value}`}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${answers[q.id] === opt.value ? (opt.value === "unknown" ? "bg-slate-500 text-white border-slate-500" : "bg-emerald-600 text-white border-emerald-600") : "bg-white text-slate-600 border-slate-300 hover:border-emerald-400"}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-2"><Paperclip className="w-4 h-4 text-emerald-600" /><h3 className="font-display font-semibold text-slate-900">Evidence files</h3></div>
          <p className="text-sm text-slate-500 mb-3">Attach supporting documents (reports, spreadsheets, policies) and tag each to a question. Findings backed by evidence are labelled "evidence-supported".</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {evidence.map((f) => (
              <span key={f.id} className="inline-flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-lg">
                <FileText className="w-3 h-3" /> {f.filename}
                <span className="text-emerald-500">· {f.category_name || "General"}</span>
                <button onClick={() => setEvidence((ev) => ev.filter((x) => x.id !== f.id))}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={tagFor} onValueChange={setTagFor}>
              <SelectTrigger className="w-72 h-9" data-testid="evidence-tag-select"><SelectValue placeholder="Tag to question" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General evidence</SelectItem>
                {flatQuestions.map((q) => <SelectItem key={q.id} value={q.id}>{q.category_name}: {q.text.slice(0, 40)}{q.text.length > 40 ? "…" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-dashed border-slate-300 hover:border-emerald-400 cursor-pointer text-slate-600" data-testid="diag-evidence-upload">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} Attach file
              <input type="file" className="hidden" onChange={onFile} disabled={uploading} />
            </label>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6 sticky bottom-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-diagnostic" disabled={!orgName || answeredCount === 0 || run.isPending}
          onClick={() => run.mutate({ org_name: orgName, answers, evidence: { files: evidence } })}>
          {run.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Score & analyse <ArrowRight className="w-4 h-4 ml-1.5" /></>}
        </Button>
      </div>
    </div>
  );
}

function DiagnosticDetail({ id, onBack, nav }) {
  const qc = useQueryClient();
  const { data: d, isLoading } = useQuery({ queryKey: ["diagnostic", id], queryFn: async () => (await api.get(`/diagnostic/${id}`)).data });
  const [summary, setSummary] = useState(null);
  const [findings, setFindings] = useState(null);

  const explain = useMutation({ mutationFn: () => api.post(`/diagnostic/${id}/explain`), onSuccess: (r) => { qc.setQueryData(["diagnostic", id], r.data); setSummary(r.data.ai_summary); setFindings(r.data.findings); toast.success("AI findings drafted — please review"); } });
  const approve = useMutation({ mutationFn: () => api.post(`/diagnostic/${id}/approve`, { ai_summary: summary ?? d.ai_summary, findings: findings ?? d.findings }), onSuccess: (r) => { qc.setQueryData(["diagnostic", id], r.data); qc.invalidateQueries({ queryKey: ["diagnostics"] }); toast.success("Diagnostic approved"); } });
  const toOpp = useMutation({ mutationFn: () => api.post(`/diagnostic/${id}/to-opportunity`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["opps"] }); toast.success("Opportunity created"); nav("/pipeline"); } });

  if (isLoading || !d) return <Loading />;
  const s = d.scoring;
  const curSummary = summary ?? d.ai_summary;
  const curFindings = findings ?? d.findings;
  const radarData = s.category_scores.map((c) => ({ subject: c.name.split(" ")[0], score: c.score_pct }));
  const evidenceCats = new Set((d.evidence?.files || []).map((f) => f.category_name).filter(Boolean));
  const gapsNeedingEvidence = s.priority_gaps.filter((g) => !evidenceCats.has(g.name));

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ChevronLeft className="w-4 h-4" /> Back to diagnostics</button>
      <PageHeader eyebrow={`Diagnostic v${d.version} · ${d.framework_version}`} title={d.org_name}
        actions={
          <div className="flex items-center gap-2">
            <a href={`${API}/diagnostic/${id}/export/pdf`} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="export-diagnostic-pdf"><Download className="w-4 h-4 mr-1.5" /> Export PDF</Button></a>
            {d.approved ? <ApprovedTag /> : <AITag />}
          </div>
        } />

      {(d.evidence?.files?.length > 0) && (
        <div className="mb-6" data-testid="diag-evidence-list">
          <div className="text-xs text-slate-400 flex items-center gap-1 mb-2"><Paperclip className="w-3.5 h-3.5" /> Evidence attached</div>
          <div className="flex flex-wrap gap-2">
            {d.evidence.files.map((f) => (
              <a key={f.id} href={`${API}/files/${f.id}/download`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 hover:border-emerald-300 px-2.5 py-1.5 rounded-lg text-slate-600">
                <FileText className="w-3 h-3 text-emerald-600" /> {f.filename}
                {f.category_name && <span className="text-emerald-600">· {f.category_name}</span>}
              </a>
            ))}
          </div>
        </div>
      )}

      {s.missing_data_warning && (
        <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6" data-testid="missing-data-warning">
          <AlertTriangle className="w-4 h-4" /> {s.missing_data_pct}% of questions were answered "Unknown". Treat gaps as data gaps, not weaknesses.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 flex flex-col items-center justify-center" data-testid="diagnostic-score-card">
          <div className="text-xs font-mono uppercase text-slate-400 mb-2">Overall maturity</div>
          <div className="font-display text-6xl font-bold text-slate-900">{s.overall_score}<span className="text-2xl text-slate-400">%</span></div>
          <div className={`text-lg font-semibold mt-1 ${LEVEL_COLORS[s.overall_level]}`}>{s.overall_level}</div>
          <div className="text-xs text-slate-400 mt-2">{s.answered} answered · {s.unknown_count} unknown</div>
        </div>
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
          <h3 className="font-display font-semibold text-slate-900 mb-2">Maturity by category</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={radarData} outerRadius="75%">
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: "#64748b" }} />
              <RadarSeries dataKey="score" stroke="#059669" fill="#059669" fillOpacity={0.25} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Category scores <span className="text-xs font-normal text-slate-400">(visible basis)</span></h3>
          <div className="space-y-3">
            {s.category_scores.map((c) => (
              <div key={c.id}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-700">{c.name} <span className="text-slate-400 text-xs">· {c.pillar}</span></span>
                  <span className={`font-semibold ${LEVEL_COLORS[c.level]}`}>{c.score_pct}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${c.score_pct}%` }} /></div>
                {c.unknown > 0 && <div className="text-[11px] text-amber-600 mt-1">{c.unknown} of {c.total} unknown</div>}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
            <h3 className="font-display font-semibold text-slate-900 mb-3">Priority gaps</h3>
            {s.priority_gaps.map((g, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-sm text-slate-700">{g.name}</span>
                <span className="text-sm font-semibold text-rose-600">{g.score_pct}%</span>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
            <h3 className="font-display font-semibold text-slate-900 mb-3">Recommended service bundles</h3>
            {d.recommended_bundles.map((b, i) => (
              <div key={i} className="mb-3 last:mb-0">
                <div className="text-sm font-medium text-emerald-700">{b.service}</div>
                <div className="text-xs text-slate-500">{b.rationale}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Evidence readiness */}
      {!d.approved && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 mt-5" data-testid="evidence-readiness">
          <div className="flex items-center gap-2 mb-1">
            <Paperclip className="w-4 h-4 text-emerald-600" />
            <h3 className="font-display font-semibold text-slate-900">Evidence readiness</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">Priority-gap areas without supporting evidence are flagged. Attaching evidence (re-run a diagnostic version) upgrades findings from "system-inferred" to "evidence-supported".</p>
          <div className="space-y-2">
            {s.priority_gaps.map((g, i) => {
              const has = evidenceCats.has(g.name);
              return (
                <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg border ${has ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid={`evidence-flag-${i}`}>
                  <span className="text-sm text-slate-700">{g.name}</span>
                  {has ? (
                    <span className="text-xs text-emerald-700 font-medium flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Evidence attached</span>
                  ) : (
                    <span className="text-xs text-amber-700 font-medium flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Evidence recommended</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI findings */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 mt-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-600" /> AI-explained findings</h3>
          {!d.approved && <Button variant="outline" size="sm" onClick={() => explain.mutate()} disabled={explain.isPending} data-testid="generate-ai-findings">{explain.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : curSummary ? "Regenerate" : "Generate with AI"}</Button>}
        </div>
        {curSummary ? (
          <>
            {!d.approved && <AITag className="mb-3" />}
            <Textarea value={curSummary} onChange={(e) => setSummary(e.target.value)} disabled={d.approved} data-testid="findings-summary" className="min-h-[160px] text-sm leading-relaxed" />
            <div className="mt-4 space-y-2">
              {(curFindings || []).map((f, i) => (
                <div key={f.id || i} className="flex items-start gap-2 text-sm p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full border flex-shrink-0 mt-0.5 ${SOURCE_STYLES[f.source] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{f.source}</span>
                  <span className="text-slate-700">{f.text}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-400">Generate an AI explanation of these scores, then review and edit before approving. AI drafts are always marked until you approve them.</p>
        )}
      </div>

      <div className="flex flex-col items-end gap-2 mt-6">
        {!d.approved && curSummary && gapsNeedingEvidence.length > 0 && (
          <div className="text-xs text-amber-700 flex items-center gap-1.5" data-testid="approve-evidence-warning">
            <AlertTriangle className="w-3.5 h-3.5" /> {gapsNeedingEvidence.length} priority-gap area(s) have no evidence attached. You can still approve using adviser judgement.
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          {!d.approved && curSummary && <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approve.mutate()} disabled={approve.isPending} data-testid="approve-diagnostic"><ShieldCheck className="w-4 h-4 mr-1.5" /> Approve findings</Button>}
          {d.approved && (
            <>
              <Button variant="outline" onClick={() => toOpp.mutate()} disabled={toOpp.isPending || d.opportunity_id} data-testid="diag-to-opportunity">{d.opportunity_id ? "Opportunity created" : "Create opportunity"}</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => nav("/proposals")} data-testid="diag-to-proposal"><FileText className="w-4 h-4 mr-1.5" /> Build proposal</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
